import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Wand2, Users, Search, ShieldOff, Loader2, Play, Sparkles, CheckCircle2,
  X, RefreshCw, Zap, Eye, RotateCw,
} from "lucide-react";

const KIT_TYPES = [
  "full_brand_kit", "website", "logo", "social_pack",
  "google_business", "video", "seo", "campaign",
];

export default function AdminMagic() {
  const [customers, setCustomers] = useState([]);
  const [query, setQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [kitType, setKitType] = useState("full_brand_kit");
  const [instructions, setInstructions] = useState("");
  const [override, setOverride] = useState(false);
  const [busy, setBusy] = useState(false);
  const [runs, setRuns] = useState([]);
  const [batches, setBatches] = useState([]);
  const [previewRunId, setPreviewRunId] = useState(null);

  const loadAll = async () => {
    try {
      const [c, r, b] = await Promise.all([
        api.get("/admin/customers"),
        api.get("/admin/magic/runs"),
        api.get("/admin/magic/bulk"),
      ]);
      setCustomers(c.data.customers || []);
      setRuns(r.data || []);
      setBatches(b.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    }
  };
  useEffect(() => { loadAll(); }, []);

  // Live polling — only when at least one batch is in-flight
  useEffect(() => {
    const hasRunning = batches.some((b) => b.status === "running");
    if (!hasRunning) return;
    const t = setInterval(loadAll, 4000);
    return () => clearInterval(t);
  }, [batches]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter(({ user, profile }) =>
      (user.email || "").toLowerCase().includes(q) ||
      (user.name || "").toLowerCase().includes(q) ||
      (profile?.business_name || "").toLowerCase().includes(q)
    );
  }, [query, customers]);

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const generateOne = async (userId) => {
    setBusy(true);
    try {
      await api.post(`/admin/magic/generate/${userId}`, {
        kit_type: kitType,
        instructions: instructions.trim() || null,
        use_saved_profile: true,
        override_limits: override,
      });
      toast.success("Kit generated · sent to customer's Pending Review");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setBusy(false); }
  };

  const generateBulk = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return toast.error("Select at least one customer");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/magic/bulk", {
        customer_ids: ids,
        kit_type: kitType,
        instructions: instructions.trim() || null,
        override_limits: override,
      });
      toast.success(`Bulk batch started · ${data.total} customers queued`);
      setSelectedIds(new Set());
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="admin-magic-root">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-fuchsia-400/80 mb-2 inline-flex items-center gap-1.5">
          <Wand2 className="h-3.5 w-3.5" /> Admin Magic Console
        </div>
        <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">Generate for any customer.</h1>
        <p className="text-white/55 mt-3 max-w-2xl text-sm">Pick customers, choose a kit, generate single or bulk. Override toggle bypasses subscription + credit gates (super-admin).</p>
      </header>

      {/* Generation control */}
      <div className="glass-strong rounded-2xl p-5 border border-fuchsia-500/20 space-y-4">
        <div className="grid lg:grid-cols-3 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold">Kit type</label>
            <select value={kitType} onChange={(e) => setKitType(e.target.value)} data-testid="admin-magic-kit"
              className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-sm">
              {KIT_TYPES.map((k) => <option key={k} value={k}>{k.replace(/_/g, " ")}</option>)}
            </select>
          </div>
          <div className="lg:col-span-2">
            <label className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold">Custom instructions (optional)</label>
            <input value={instructions} onChange={(e) => setInstructions(e.target.value)} data-testid="admin-magic-instructions"
              placeholder="e.g. Focus on Bangalore market, youthful tone"
              className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-sm" />
          </div>
        </div>
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} data-testid="admin-magic-override"
            className="accent-fuchsia-500" />
          <ShieldOff className="h-4 w-4 text-amber-400" />
          <span><strong className="text-amber-300">Override limits</strong> — bypass subscription + credit gates (super-admin only)</span>
        </label>
      </div>

      {/* Customer table */}
      <div className="glass rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between gap-3 p-4 border-b border-white/5 flex-wrap">
          <div className="inline-flex items-center gap-2">
            <Users className="h-4 w-4 text-cyan-400" />
            <span className="text-sm text-white/70 font-semibold">{filtered.length} customers</span>
            {selectedIds.size > 0 && <span className="text-xs text-fuchsia-300 ml-3">{selectedIds.size} selected</span>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="h-4 w-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name / email / business"
                data-testid="admin-magic-search"
                className="pl-9 pr-3 py-2 bg-black/40 border border-white/10 rounded-lg text-sm w-72" />
            </div>
            <button
              onClick={generateBulk}
              disabled={busy || selectedIds.size === 0}
              data-testid="admin-magic-bulk-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold bg-gradient-to-r from-fuchsia-500 to-cyan-500 hover:opacity-95 disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />} Bulk generate ({selectedIds.size})
            </button>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-[0.2em] text-white/40 bg-white/[0.02]">
              <th className="py-2 px-3 w-10"></th>
              <th className="py-2 px-3">Customer</th>
              <th className="py-2 px-3">Status</th>
              <th className="py-2 px-3">Plan</th>
              <th className="py-2 px-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(({ user, subscription, profile }) => (
              <tr key={user.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`admin-magic-row-${user.id}`}>
                <td className="py-2 px-3">
                  <input type="checkbox" checked={selectedIds.has(user.id)} onChange={() => toggle(user.id)}
                    data-testid={`admin-magic-pick-${user.id}`} className="accent-fuchsia-500" />
                </td>
                <td className="py-2 px-3">
                  <div className="font-semibold">{profile?.business_name || user.name}</div>
                  <div className="text-xs text-white/40 font-mono">{user.email}</div>
                </td>
                <td className="py-2 px-3">
                  <span className="text-[10px] uppercase tracking-[0.2em] font-mono">{user.status || "—"}</span>
                </td>
                <td className="py-2 px-3 text-xs">{subscription?.plan_name || <span className="text-white/30">—</span>}</td>
                <td className="py-2 px-3 text-right">
                  <button
                    onClick={() => generateOne(user.id)}
                    disabled={busy}
                    data-testid={`admin-magic-gen-${user.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs glass hover:bg-white/5 disabled:opacity-50"
                  >
                    <Play className="h-3 w-3 text-fuchsia-300" /> Generate
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-white/40 text-sm">No customers</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Bulk batches */}
      {batches.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold mb-3">Recent bulk batches</div>
          <div className="space-y-2">
            {batches.slice(0, 10).map((b) => (
              <div key={b.id} className="glass rounded-xl p-3 flex items-center justify-between text-sm" data-testid={`admin-magic-batch-${b.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-bold inline-flex items-center gap-2">
                    {b.kit_type.replace(/_/g, " ")} · {b.total} customers
                    {b.status === "running" && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-cyan-300">
                        <Loader2 className="h-3 w-3 animate-spin" /> live
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-white/40 font-mono">
                    {new Date(b.created_at).toLocaleString()}
                    {b.rerun_of && <span className="ml-2 text-amber-400">· rerun</span>}
                  </div>
                  {b.status === "running" && b.total > 0 && (
                    <div className="mt-1.5 h-1 w-full bg-white/5 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-fuchsia-500 to-cyan-500 transition-all"
                        style={{ width: `${Math.min(100, ((b.progress || 0) / b.total) * 100)}%` }}
                      />
                    </div>
                  )}
                </div>
                <div className="text-right ml-3 flex flex-col items-end gap-1">
                  <div className="text-sm">
                    <span className="text-emerald-300">{b.success}</span> /{" "}
                    <span className="text-rose-300">{b.failed}</span> /{" "}
                    <span className="text-white/60">{b.total}</span>
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">{b.status}</div>
                  {b.status === "complete" && b.failed > 0 && (
                    <RerunFailedBtn batchId={b.id} failed={b.failed} onDone={loadAll} />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent runs */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold">Recent generations</div>
          <button onClick={loadAll} className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1">
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>
        {runs.length === 0 ? (
          <div className="glass rounded-2xl p-10 text-center text-white/50 text-sm">No runs yet.</div>
        ) : (
          <div className="space-y-2">
            {runs.slice(0, 30).map((r) => (
              <div key={r.id} className="glass rounded-xl p-3 flex items-center justify-between text-sm" data-testid={`admin-magic-run-${r.id}`}>
                <div className="min-w-0">
                  <div className="font-bold">{r.kit_type.replace(/_/g, " ")}</div>
                  <div className="text-xs text-white/40 font-mono mt-0.5">
                    user:{r.user_id?.slice(0, 8)} · {new Date(r.created_at).toLocaleString()}
                    {r.override_limits && <span className="ml-2 text-amber-400">· OVERRIDE</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded font-bold ${
                    r.status === "published" ? "bg-cyan-500/15 text-cyan-300" :
                    r.status === "approved" ? "bg-emerald-500/15 text-emerald-300" :
                    r.status === "rejected" ? "bg-rose-500/15 text-rose-300" :
                    "bg-amber-500/15 text-amber-300"
                  }`}>{r.status}</span>
                  <button
                    onClick={() => setPreviewRunId(r.id)}
                    data-testid={`admin-magic-preview-${r.id}`}
                    title="Preview generated kit"
                    className="p-1 rounded hover:bg-white/5"
                  >
                    <Eye className="h-3.5 w-3.5 text-cyan-300" />
                  </button>
                  {r.status === "pending_review" && (
                    <PublishBtn runId={r.id} onPublished={loadAll} />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {previewRunId && <KitPreviewDrawer runId={previewRunId} onClose={() => setPreviewRunId(null)} />}
    </div>
  );
}

function RerunFailedBtn({ batchId, failed, onDone }) {
  const [busy, setBusy] = useState(false);
  const rerun = async () => {
    if (!window.confirm(`Re-run ${failed} failed customer(s)?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/magic/bulk/${batchId}/rerun-failed`);
      toast.success(`Re-run started · ${data.total} queued`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-run failed");
    } finally { setBusy(false); }
  };
  return (
    <button
      onClick={rerun}
      disabled={busy}
      data-testid={`admin-magic-rerun-${batchId}`}
      className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold bg-amber-500/15 border border-amber-400/40 hover:bg-amber-500/25 text-amber-300 disabled:opacity-50"
    >
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3" />}
      Re-run failed
    </button>
  );
}

function KitPreviewDrawer({ runId, onClose }) {
  const [run, setRun] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/magic/${runId}`).then((r) => setRun(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load"));
  }, [runId]);
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/70" onClick={onClose}>
      <div className="bg-[#0c0626] border-l border-fuchsia-500/30 h-full w-full max-w-2xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="admin-magic-preview-drawer">
        <div className="shrink-0 flex items-center justify-between p-4 border-b border-white/10">
          <h3 className="font-heading text-lg font-black inline-flex items-center gap-2">
            <Eye className="h-5 w-5 text-cyan-400" /> Kit preview
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 sidebar-scroll">
          {err && <div className="text-rose-300 text-sm">{err}</div>}
          {!run && !err && <div className="text-white/40 inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
          {run && (
            <>
              <div className="text-xs text-white/40 font-mono mb-1">user:{run.user_id?.slice(0, 8)} · {run.kit_type}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-3">{new Date(run.created_at).toLocaleString()}</div>
              {run.logo_image_b64 && (
                <div className="bg-white rounded-xl p-4 max-w-xs mb-4">
                  <img src={`data:image/png;base64,${run.logo_image_b64}`} alt="logo" className="w-full" />
                </div>
              )}
              <pre className="glass rounded-xl p-4 text-xs whitespace-pre-wrap break-words text-white/85">{JSON.stringify(run.result, null, 2)}</pre>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PublishBtn({ runId, onPublished }) {
  const [busy, setBusy] = useState(false);
  const publish = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/magic/${runId}/approve-and-publish`, { cadence_hours: 24 });
      toast.success(`${data.scheduled_count} posts scheduled · first at ${new Date(data.first_at).toLocaleString()}`);
      onPublished?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally { setBusy(false); }
  };
  return (
    <button
      onClick={publish}
      disabled={busy}
      data-testid={`admin-magic-publish-${runId}`}
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-bold bg-gradient-to-r from-cyan-500/30 to-fuchsia-500/30 border border-cyan-400/40 hover:bg-cyan-500/20 disabled:opacity-50"
    >
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
      Approve & Publish
    </button>
  );
}
